import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import MovieIcon from "@mui/icons-material/Movie";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import ShareIcon from "@mui/icons-material/Share";
import { toAbsoluteMediaUrl } from "../utils/kotPlayerMatches";
import { hasOrientVideoTransform } from "../utils/videoViewSetup";
import { isMaxRallyEvaluationVideo, isStrokeSlowMoEvaluationVideo, resolveEvaluationVideoReadyWall, formatEvalWallMs } from "../utils/evaluationVideoMediaMeta";
import { formatReencodeEta } from "../api/evaluationVideoBakeApi";
import { ip_back } from "../config";

function parseBakeVersionMeta(raw) {
  if (!raw) return null;
  let data = raw;
  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") return null;
  const version = Number(data.bake_version ?? data.bakeVersion);
  if (!Number.isFinite(version) || version < 2) return null;
  const wallMs = Number(data.bake_wall_ms ?? data.bakeWallMs);
  const quality = String((data.bake_quality ?? data.bakeQuality) || "").trim();
  return {
    version: Math.floor(version),
    wallMs: Number.isFinite(wallMs) && wallMs > 0 ? wallMs : null,
    quality: quality || null,
    parentMessageId: data.parent_message_id || data.parentMessageId || null,
  };
}

function formatBakeWallMs(ms) {
  return formatEvalWallMs(ms);
}

export function formatEvaluationVideoDescription(msg, t) {
  if (isStrokeSlowMoEvaluationVideo(msg)) {
    return t(
      "strokeSlowMoFootnote",
      "Portrait slow-mo of the best stroke in the max rally"
    );
  }
  const text = (msg?.text_message || "").trim();
  if (msg?.kind === "table_replay" || text === "Table replay") {
    return t(
      "liveTrainingReplaySavedFootnote",
      "Saved live-training table replay (last 10 seconds)"
    );
  }
  if (!text || /^kot_match_id:/i.test(text)) {
    return t(
      "evaluationVideoFootnote",
      "Full high-speed camera footage recording"
    );
  }
  return text;
}

function jobProgressPct(job) {
  return Math.round(
    Math.min(100, Math.max(0, (Number(job?.progress) || 0) * 100))
  );
}

function jobEtaLabel(job, t) {
  const compact = formatReencodeEta(job?.etaSeconds);
  if (!compact) return "";
  return t("sessionMediaEtaShort", "{{eta}} left", { eta: compact });
}

/**
 * Shared Media-tab evaluation / KoT video card.
 * Used by exercise Media tab and session media dialog.
 */
export function EvaluationVideoMediaCard({
  msg,
  meta,
  formattedDate,
  videoUrl,
  playLabel,
  onPlay,
  canMoveVideo = false,
  onMoveVideo,
  canBenchmark = false,
  onBenchmark,
  canBakeOrient = false,
  bakeOrientBusy = false,
  bakingMessageId = null,
  onBakeOrient,
  canDetectStrokes = false,
  detectStrokesBusy = false,
  detectingMessageId = null,
  onDetectStrokes,
  jobStatus = null,
  isActivePlayback = false,
  /** Pose3D dual-select: null | 0 | 1 (cam index), or false to hide */
  pose3dSelectIndex = null,
  pose3dSelectEnabled = false,
  onPose3dSelect = null,
  canShareReel = false,
  onShareReel = null,
  /** "compact" is the session-media list: one dense row, play on the left. */
  density = "comfortable",
  isReturnPulse = false,
  t = (k, d) => d || k,
}) {
  const roleLines = Array.isArray(meta?.roleLines) ? meta.roleLines : [];
  const lastRoleIndex = roleLines.length - 1;
  const exerciseLabel = meta?.exerciseLabel || null;
  const detailParts = [
    meta?.roundLabel || null,
    meta?.tableLabel,
    meta?.cameraSetupLabel || meta?.cameraPosition || null,
    formattedDate || null,
  ].filter(Boolean);
  const headerParts = [exerciseLabel, ...detailParts].filter(Boolean);
  const cardClass = [
    "player-exercise-details__eval-video-card",
    "player-exercise-details__eval-video-card--lines",
    isActivePlayback
      ? "player-exercise-details__eval-video-card--playing"
      : "",
    jobStatus?.status === "running"
      ? "player-exercise-details__eval-video-card--processing"
      : "",
    pose3dSelectIndex === 0 || pose3dSelectIndex === 1
      ? "player-exercise-details__eval-video-card--pose3d-selected"
      : "",
  ]
    .filter(Boolean)
    .join(" ");
  const showBake =
    canBakeOrient &&
    msg?.message_id &&
    hasOrientVideoTransform(msg?.view_setup);
  const isBakingThis =
    bakingMessageId != null &&
    String(bakingMessageId) === String(msg?.message_id);
  const isDetectingThis =
    detectingMessageId != null &&
    String(detectingMessageId) === String(msg?.message_id);
  const anyBusy = Boolean(bakeOrientBusy || detectStrokesBusy);
  const pct = jobProgressPct(jobStatus);
  const eta = jobEtaLabel(jobStatus, t);
  const jobKindLabel =
    jobStatus?.kind === "reencode"
      ? t("bakeOrientsWorking", "Re-encoding…")
      : jobStatus?.kind === "pose"
        ? t("sessionMediaPoseWorking", "Detecting strokes…")
        : "";
  const bakeMeta = parseBakeVersionMeta(msg?.view_setup);
  const bakeWallLabel = bakeMeta
    ? formatBakeWallMs(bakeMeta.wallMs)
    : null;
  const readyWall = resolveEvaluationVideoReadyWall(msg);
  const poseWallMs = (() => {
    try {
      const setup =
        typeof msg?.view_setup === "string"
          ? JSON.parse(msg.view_setup)
          : msg?.view_setup;
      const n = Number(setup?.pose_wall_ms);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  })();
  const poseWallLabel = poseWallMs != null ? formatEvalWallMs(poseWallMs) : null;

  const clipTools = (
    <ClipTools
      msg={msg}
      videoUrl={videoUrl}
      t={t}
      pose3dSelectEnabled={pose3dSelectEnabled}
      pose3dSelectIndex={pose3dSelectIndex}
      onPose3dSelect={onPose3dSelect}
      canBenchmark={canBenchmark}
      onBenchmark={onBenchmark}
      showBake={showBake}
      anyBusy={anyBusy}
      bakeLocked={Boolean(bakingMessageId)}
      isBakingThis={isBakingThis}
      onBakeOrient={onBakeOrient}
      canDetectStrokes={canDetectStrokes}
      detectLocked={Boolean(detectingMessageId)}
      isDetectingThis={isDetectingThis}
      onDetectStrokes={onDetectStrokes}
      canMoveVideo={canMoveVideo}
      onMoveVideo={onMoveVideo}
      canShareReel={canShareReel}
      onShareReel={onShareReel}
    />
  );

  if (density === "compact") {
    const kind = resolveSessionClipKind(msg, meta, t);
    const title =
      meta?.title ||
      exerciseLabel ||
      t("evaluationVideo", "Evaluation Video");
    const metaLine = [
      ...detailParts,
      meta?.isLefty ? meta.leftyLabel || t("leftyCameraBadge", "Lefty cam") : null,
      bakeMeta
        ? `v${bakeMeta.version}${bakeWallLabel ? ` · ${bakeWallLabel}` : ""}`
        : null,
      readyWall?.pending
        ? t("evalVideoReadyProcessing", "Processing…")
        : readyWall?.label
          ? t("evalVideoReadyWall", "Ready in {{wall}}", { wall: readyWall.label })
          : null,
      poseWallLabel
        ? t("evalVideoPoseWall", "Pose {{wall}}", { wall: poseWallLabel })
        : null,
    ]
      .filter(Boolean)
      .join(" · ");
    const roleLine = roleLines
      .map((line) =>
        `${line.slot} ${line.name}${line.role ? ` · ${line.role}` : ""}`.trim()
      )
      .filter(Boolean)
      .join("  ·  ");
    const playClip = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!videoUrl) return;
      onPlay?.(videoUrl, meta?.title, msg?.message_id || null);
    };
    const clipClass = [
      "session-media-clip",
      isActivePlayback ? "session-media-clip--last" : "",
      isReturnPulse ? "session-media-clip--return" : "",
      jobStatus?.status === "running" ? "session-media-clip--processing" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <article
        className={clipClass}
        data-session-clip-id={msg?.message_id ?? ""}
        data-kind={kind.key}
      >
        <button
          type="button"
          className="session-media-clip__play"
          onClick={playClip}
          disabled={!videoUrl}
          aria-label={playLabel}
        >
          <PlayCircleFilledIcon style={{ fontSize: 22 }} />
        </button>
        <div
          className="session-media-clip__body"
          onClick={videoUrl ? playClip : undefined}
        >
          <span className="session-media-clip__title-row">
            <span className="session-media-clip__title">{title}</span>
            <span className="session-media-clip__kind">{kind.label}</span>
            {isActivePlayback ? (
              <span className="session-media-clip__watched">
                {t("sessionMediaLastWatched", "Last watched")}
              </span>
            ) : null}
          </span>
          {metaLine ? (
            <span className="session-media-clip__meta">{metaLine}</span>
          ) : null}
          {roleLine ? (
            <span className="session-media-clip__roles">{roleLine}</span>
          ) : null}
        </div>
        <div className="session-media-clip__tools">{clipTools}</div>
        <ClipJobStatus jobStatus={jobStatus} jobKindLabel={jobKindLabel} pct={pct} eta={eta} t={t} />
      </article>
    );
  }

  return (
    <div className={cardClass}>
      <div className="player-exercise-details__eval-video-line player-exercise-details__eval-video-line--header">
        <div className="player-exercise-details__eval-video-line-main">
          <MovieIcon
            className="player-exercise-details__eval-video-line-icon"
            style={{ fontSize: 18 }}
            aria-hidden
          />
          <span className="player-exercise-details__eval-video-line-summary">
            {headerParts.length > 0
              ? headerParts.join(" · ")
              : meta?.title || t("evaluationVideo", "Evaluation Video")}
          </span>
          {bakeMeta ? (
            <span
              className="player-exercise-details__eval-video-kot-badge"
              title={
                bakeWallLabel
                  ? t("bakeVersionWallHint", "Bake wall time {{wall}}", {
                      wall: bakeWallLabel,
                    })
                  : t("bakeVersionHint", "Baked transform version")
              }
            >
              {`v${bakeMeta.version}`}
              {bakeWallLabel ? ` · ${bakeWallLabel}` : ""}
              {bakeMeta.quality ? ` · ${bakeMeta.quality}` : ""}
            </span>
          ) : null}
          {readyWall?.pending ? (
            <span
              className="player-exercise-details__eval-video-ready-badge player-exercise-details__eval-video-ready-badge--pending"
              title={t(
                "evalVideoReadyWallHint",
                "Upload and processing until the clip is ready to play"
              )}
            >
              {t("evalVideoReadyProcessing", "Processing…")}
            </span>
          ) : readyWall?.label ? (
            <span
              className="player-exercise-details__eval-video-ready-badge"
              title={t(
                "evalVideoReadyWallHint",
                "Upload and processing until the clip is ready to play"
              )}
            >
              {t("evalVideoReadyWall", "Ready in {{wall}}", {
                wall: readyWall.label,
              })}
            </span>
          ) : null}
          {poseWallLabel ? (
            <span
              className="player-exercise-details__eval-video-ready-badge"
              title={t(
                "evalVideoPoseWallHint",
                "Wall time for pose processing on this clip"
              )}
            >
              {t("evalVideoPoseWall", "Pose {{wall}}", {
                wall: poseWallLabel,
              })}
            </span>
          ) : null}
          {isStrokeSlowMoEvaluationVideo(msg) ? (
            <span
              className="player-exercise-details__eval-video-kot-badge"
              title={t(
                "strokeSlowMoFootnote",
                "Portrait slow-mo of the best stroke in the max rally"
              )}
            >
              {t("strokeSlowMoShort", "Slow-mo reel")}
            </span>
          ) : null}
          {isMaxRallyEvaluationVideo(msg) ? (
            <span
              className="player-exercise-details__eval-video-kot-badge"
              title={t(
                "maxRallyReelFootnote",
                "Portrait reel of the longest rally"
              )}
            >
              {t("maxRallyReelShort", "Reel Max Rally")}
            </span>
          ) : null}
          {meta?.isKotMatch ? (
            <span className="player-exercise-details__eval-video-kot-badge">
              {t("kotShort", "KoT")}
            </span>
          ) : null}
          {meta?.isLefty && (
            <span className="player-exercise-details__eval-video-lefty-badge">
              {meta.leftyLabel || "Lefty"}
            </span>
          )}
        </div>
        <div className="player-exercise-details__eval-video-header-actions">
          {clipTools}
        </div>
      </div>

      {roleLines.length > 0 ? (
        roleLines.map((line, index) => {
          const showPlay = Boolean(videoUrl) && index === lastRoleIndex;
          return (
            <div
              key={line.slot}
              className="player-exercise-details__eval-video-line player-exercise-details__eval-video-line--role"
            >
              <div className="player-exercise-details__eval-video-line-main">
                <span className="player-exercise-details__eval-video-role-slot">
                  {line.slot}:
                </span>
                <span
                  className={[
                    "player-exercise-details__eval-video-role-name",
                    line.isViewer
                      ? "player-exercise-details__eval-video-role-name--viewer"
                      : "",
                    line.isActive
                      ? "player-exercise-details__eval-video-role-name--active"
                      : "",
                    line.isSparring
                      ? "player-exercise-details__eval-video-role-name--sparring"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {line.name}
                  {line.role ? ` - ${line.role}` : ""}
                </span>
              </div>
              {showPlay && (
                <button
                  type="button"
                  className="player-exercise-details__video-play-btn player-exercise-details__video-play-btn--inline"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onPlay(videoUrl, meta?.title, msg?.message_id || null);
                  }}
                  aria-label={playLabel}
                >
                  <PlayCircleFilledIcon style={{ fontSize: 22 }} />
                </button>
              )}
            </div>
          );
        })
      ) : videoUrl ? (
        <div className="player-exercise-details__eval-video-line player-exercise-details__eval-video-line--role">
          <div className="player-exercise-details__eval-video-line-main">
            <span className="player-exercise-details__eval-video-role-name">
              {formatEvaluationVideoDescription(msg, t)}
            </span>
          </div>
          <button
            type="button"
            className="player-exercise-details__video-play-btn player-exercise-details__video-play-btn--inline"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onPlay(videoUrl, meta?.title, msg?.message_id || null);
            }}
            aria-label={playLabel}
          >
            <PlayCircleFilledIcon style={{ fontSize: 22 }} />
          </button>
        </div>
      ) : null}

      {jobStatus &&
      (jobStatus.status === "running" ||
        jobStatus.status === "error" ||
        jobStatus.status === "done") ? (
        <div className="player-exercise-details__eval-video-job">
          {jobStatus.status === "running" ? (
            <>
              <div className="player-exercise-details__eval-video-job-label">
                <span>{jobKindLabel}</span>
                <span>
                  {pct}%
                  {eta ? ` · ${eta}` : ""}
                </span>
              </div>
              <div
                className="player-exercise-details__eval-video-job-track"
                aria-hidden
              >
                <div
                  className="player-exercise-details__eval-video-job-fill"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </>
          ) : jobStatus.status === "error" ? (
            <p className="player-exercise-details__eval-video-job-error">
              {jobStatus.error}
            </p>
          ) : (
            <p className="player-exercise-details__eval-video-job-done">
              {jobStatus.kind === "reencode"
                ? t("bakeOrientsDoneOne", "Re-encode complete")
                : t("sessionMediaPoseDone", "Stroke detection complete")}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function resolveSessionClipKind(msg, meta, t) {
  if (isStrokeSlowMoEvaluationVideo(msg) || isMaxRallyEvaluationVideo(msg)) {
    return {
      key: "reel",
      label: isStrokeSlowMoEvaluationVideo(msg)
        ? t("strokeSlowMoShort", "Slow-mo")
        : t("maxRallyReelShort", "Reel"),
    };
  }
  if (msg?.kind === "table_replay") {
    return { key: "replay", label: t("sessionMediaFilterReplay", "Replay") };
  }
  if (msg?.kind === "kot" || msg?.is_kot || meta?.isKotMatch) {
    return { key: "kot", label: t("kotShort", "KoT") };
  }
  return { key: "eval", label: t("sessionMediaFilterEval", "Eval") };
}

function ClipJobStatus({ jobStatus, jobKindLabel, pct, eta, t }) {
  if (
    !jobStatus ||
    (jobStatus.status !== "running" &&
      jobStatus.status !== "error" &&
      jobStatus.status !== "done")
  ) {
    return null;
  }
  return (
    <div className="player-exercise-details__eval-video-job session-media-clip__job">
      {jobStatus.status === "running" ? (
        <>
          <div className="player-exercise-details__eval-video-job-label">
            <span>{jobKindLabel}</span>
            <span>
              {pct}%
              {eta ? ` · ${eta}` : ""}
            </span>
          </div>
          <div className="player-exercise-details__eval-video-job-track" aria-hidden>
            <div
              className="player-exercise-details__eval-video-job-fill"
              style={{ width: `${pct}%` }}
            />
          </div>
        </>
      ) : jobStatus.status === "error" ? (
        <p className="player-exercise-details__eval-video-job-error">
          {jobStatus.error}
        </p>
      ) : (
        <p className="player-exercise-details__eval-video-job-done">
          {jobStatus.kind === "reencode"
            ? t("bakeOrientsDoneOne", "Re-encode complete")
            : t("sessionMediaPoseDone", "Stroke detection complete")}
        </p>
      )}
    </div>
  );
}

function ClipTools({
  msg,
  videoUrl,
  t,
  pose3dSelectEnabled,
  pose3dSelectIndex,
  onPose3dSelect,
  canBenchmark,
  onBenchmark,
  showBake,
  anyBusy,
  bakeLocked,
  isBakingThis,
  onBakeOrient,
  canDetectStrokes,
  detectLocked,
  isDetectingThis,
  onDetectStrokes,
  canMoveVideo,
  onMoveVideo,
  canShareReel,
  onShareReel,
}) {
  return (
    <>
      {pose3dSelectEnabled ? (
        <button
          type="button"
          className={[
            "player-exercise-details__move-video-icon-btn",
            pose3dSelectIndex === 0 || pose3dSelectIndex === 1
              ? "player-exercise-details__move-video-icon-btn--active"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={(e) => {
            e.stopPropagation();
            onPose3dSelect?.(msg);
          }}
          aria-pressed={pose3dSelectIndex === 0 || pose3dSelectIndex === 1}
          aria-label={t("pose3dSelectVideo", "Select for Pose3D")}
          title={
            pose3dSelectIndex === 0
              ? t("pose3dCam0", "Cam 0 (primary)")
              : pose3dSelectIndex === 1
                ? t("pose3dCam1", "Cam 1 (peer)")
                : t(
                    "pose3dSelectHint",
                    "Select up to 2 videos for dual-camera Pose3D"
                  )
          }
        >
          <span style={{ fontSize: 11, fontWeight: 800 }}>
            {pose3dSelectIndex === 0 ? "C0" : pose3dSelectIndex === 1 ? "C1" : "3D"}
          </span>
        </button>
      ) : null}
      {canBenchmark && msg?.message_id ? (
        <button
          type="button"
          className="player-exercise-details__move-video-icon-btn"
          onClick={(e) => {
            e.stopPropagation();
            const url =
              videoUrl ||
              toAbsoluteMediaUrl(msg?.video_message_url, ip_back) ||
              msg?.video_message_url ||
              null;
            onBenchmark?.(msg, url);
          }}
          aria-label={t("benchmarkVideo", "Benchmark")}
          title={t(
            "benchmarkVideoHint",
            "Run pose precision benchmark with saved calibration"
          )}
        >
          <ScienceOutlinedIcon style={{ fontSize: 20 }} />
        </button>
      ) : null}
      {showBake ? (
        <button
          type="button"
          className="player-exercise-details__move-video-icon-btn"
          disabled={anyBusy || bakeLocked}
          onClick={() => onBakeOrient?.(msg)}
          aria-label={t("bakeOrientOne", "Re-encode")}
          title={t("bakeOrientOneHint", "Bake rotation/flip into this video file")}
        >
          {isBakingThis ? (
            <CircularProgress size={16} style={{ color: "#0A84FF" }} />
          ) : (
            <span style={{ fontSize: 11, fontWeight: 700 }}>RE</span>
          )}
        </button>
      ) : null}
      {canDetectStrokes && msg?.message_id ? (
        <button
          type="button"
          className="player-exercise-details__move-video-icon-btn"
          disabled={anyBusy || detectLocked}
          onClick={() => onDetectStrokes?.(msg)}
          aria-label={t("sessionMediaPose", "Detect strokes")}
          title={t(
            "sessionMediaPoseHint",
            "Run stroke detection using table calibration and analysis settings"
          )}
        >
          {isDetectingThis ? (
            <CircularProgress size={16} style={{ color: "#0A84FF" }} />
          ) : (
            <AccessibilityNewIcon style={{ fontSize: 18 }} />
          )}
        </button>
      ) : null}
      {canMoveVideo && msg?.message_id ? (
        <button
          type="button"
          className="player-exercise-details__move-video-icon-btn"
          onClick={(e) => {
            e.stopPropagation();
            onMoveVideo?.(msg);
          }}
          aria-label={t("moveVideo", "Move")}
          title={t("moveVideoUpdateParams", "Update video parameters")}
        >
          <DriveFileMoveOutlinedIcon style={{ fontSize: 20 }} />
        </button>
      ) : null}
      {(canShareReel || onShareReel) &&
      (isMaxRallyEvaluationVideo(msg) || isStrokeSlowMoEvaluationVideo(msg)) &&
      (videoUrl || msg?.video_message_url) ? (
        <button
          type="button"
          className="player-exercise-details__move-video-icon-btn"
          onClick={(e) => {
            e.stopPropagation();
            onShareReel?.(msg, videoUrl || msg?.video_message_url);
          }}
          aria-label={t("shareReel", "Share reel")}
          title={
            isMaxRallyEvaluationVideo(msg)
              ? t(
                  "shareMaxRallyHint",
                  "Share this Max Rally reel to your social networks or download it"
                )
              : t(
                  "shareReelHintPersonal",
                  "Share this reel to Instagram, TikTok, WhatsApp, or download it."
                )
          }
          style={{
            color: isMaxRallyEvaluationVideo(msg) ? "#E1306C" : undefined,
            borderColor: isMaxRallyEvaluationVideo(msg)
              ? "rgba(225, 48, 108, 0.4)"
              : undefined,
            background: isMaxRallyEvaluationVideo(msg)
              ? "rgba(225, 48, 108, 0.1)"
              : undefined,
          }}
        >
          <ShareIcon style={{ fontSize: 18 }} />
        </button>
      ) : null}
    </>
  );
}

export default EvaluationVideoMediaCard;
